import { BaseModal, BreadCrumbs, CheckBox, HomeIcon, PaginatorWrapper, Table, TextHeading } from '@isdd/idsk-ui-kit/index'
import { BASE_PAGE_NUMBER, BASE_PAGE_SIZE, DEFAULT_PAGESIZE_OPTIONS, HowTo } from '@isdd/metais-common/constants'
import { RouteNames, RouterRoutes } from '@isdd/metais-common/navigation/routeNames'
import { getHowToTranslate } from '@isdd/metais-common/utils/utils'
import React, { useRef, useState } from 'react'
import { CHECKBOX_CELL } from '@isdd/idsk-ui-kit/table/constants'
import {
    getGetRoleParticipantQueryKey,
    getReadConfigurationItemQueryKey,
    RelationshipUi,
    RelListFilterContainerUi,
    useGetRoleParticipant,
    useReadConfigurationItem,
    useReadRelationship,
} from '@isdd/metais-common/api/generated/cmdb-swagger'
import { ColumnDef, Row, CellContext } from '@tanstack/react-table'
import { Trans, useTranslation } from 'react-i18next'
import { DynamicFilterAttributes, ExtendedAttribute } from '@isdd/metais-common/components/dynamicFilterAttributes/DynamicFilterAttributes'
import { AttributeProfile, RelationshipType } from '@isdd/metais-common/api/generated/types-repo-swagger'
import { EnumType } from '@isdd/metais-common/api/generated/enums-repo-swagger'
import { Filter } from '@isdd/idsk-ui-kit/filter'
import { IFilter } from '@isdd/idsk-ui-kit/types'
import { ActionNames, ActionsOverTable, ImportButton, ModalButtons, MutationFeedback, QueryFeedback } from '@isdd/metais-common/index'
import { CiLazySelect } from '@isdd/metais-common/components/ci-lazy-select/CiLazySelect'
import { useRowSelection } from '@isdd/metais-common/hooks/useRowSelection'
import { ElementToScrollTo } from '@isdd/metais-common/components/element-to-scroll-to/ElementToScrollTo'
import { useActionSuccess } from '@isdd/metais-common/contexts/actionSuccess/actionSuccessContext'
import { useDetailData } from '@isdd/metais-common/hooks/useDetailData'

import RelationsExport from '../relation-list/RelationsExport'
import { RelationDetailModalView } from '../relation-list/RelationDetailModalView'

import { MainContentWrapper } from '@/components/MainContentWrapper'
import { RelationshipsFilterData } from '@/components/containers/relation-list/RelationListContainer'
import { RelationFilterData } from '@/components/containers/RelationTypeListContainer'
import { ColumnsOutputDefinition } from '@/componentHelpers/ci/ciTableHelpers'

export interface IRelationTypeListView {
    relationTypeData: RelationshipType
    relations: RelationshipUi[]
    attributes: ExtendedAttribute[] | undefined
    attributeProfiles: AttributeProfile[] | undefined
    constraintsData: (EnumType | undefined)[]
    defaultFilterValues: RelationFilterData
    handleFilterChange: (changedFilter: IFilter) => void
    filter: RelationshipsFilterData
    totalItems: number
    totalPages: number
    isLoading: boolean
    isError: boolean
    exportPayload: RelListFilterContainerUi
}

export const RelationTypeListView: React.FC<IRelationTypeListView> = ({
    relationTypeData,
    relations,
    attributeProfiles,
    attributes,
    constraintsData,
    defaultFilterValues,
    filter: defFilter,
    handleFilterChange,
    totalItems,
    totalPages,
    isLoading,
    isError,
    exportPayload,
}) => {
    const { t } = useTranslation()

    const tableRef = useRef<HTMLTableElement>(null)

    const rowSelection = useRowSelection<object>()

    const [relationData, setRelationData] = useState<RelationshipUi | null>(null)

    const { isActionSuccess } = useActionSuccess()

    const { technicalName, sources, targets } = relationTypeData
    const { technicalName: sourceTechName = '', name: sourceName = '' } = sources && sources.length > 0 ? sources[0] : {}
    const { technicalName: targetTechName = '', name: targetName = '' } = targets && targets.length > 0 ? targets[0] : {}

    const {
        data: relationshipData,
        isLoading: isRelationshipLoading,
        isError: isRelationshipError,
        refetch: refetchRelationship,
    } = useReadRelationship(relationData?.uuid ?? '')
    const {
        data: ciTargetData,
        isLoading: isCiTargetDataLoading,
        isError: isCiTargetDataError,
    } = useReadConfigurationItem(relationData?.endUuid ?? '', {
        query: { queryKey: getReadConfigurationItemQueryKey(relationData?.endUuid ?? ''), enabled: !!relationData?.endUuid },
    })
    const {
        data: ciSourceData,
        isLoading: isCiSourceDataLoading,
        isError: isCiSourceDataError,
    } = useReadConfigurationItem(relationData?.startUuid ?? '', {
        query: { queryKey: getReadConfigurationItemQueryKey(relationData?.startUuid ?? ''), enabled: !!relationData?.startUuid },
    })
    const {
        isLoading: isDetailDataLoading,
        isError: isDetailDataError,
        unitsData,
    } = useDetailData({
        entityStructure: relationTypeData,
        isEntityStructureLoading: isLoading,
        isEntityStructureError: isError,
    })
    const {
        data: ownerData,
        isLoading: isOwnerLoading,
        isError: isOwnerError,
    } = useGetRoleParticipant(relationData?.metaAttributes?.owner ?? '', {
        query: { queryKey: getGetRoleParticipantQueryKey(relationData?.metaAttributes?.owner ?? ''), enabled: !!relationData?.metaAttributes?.owner },
    })

    const isDetailLoading = [isRelationshipLoading, isCiTargetDataLoading, isCiSourceDataLoading, isDetailDataLoading, isOwnerLoading].some(
        (item) => item,
    )
    const isDetailError = [isRelationshipError, isCiTargetDataError, isCiSourceDataError, isDetailDataError, isOwnerError].some((item) => item)

    const columns: Array<ColumnDef<RelationshipUi>> = [
        {
            header: ({ table }) => {
                return (
                    <div className="govuk-checkboxes govuk-checkboxes--small">
                        <CheckBox
                            label=""
                            name="checkbox"
                            id="checkbox-all"
                            value="checkbox-all"
                            onChange={table.getToggleAllRowsSelectedHandler()}
                            onClick={(event) => event.stopPropagation()}
                            checked={table.getIsAllRowsSelected()}
                            aria-label={t('table.selectAllItems')}
                        />
                    </div>
                )
            },
            id: CHECKBOX_CELL,
            cell: ({ row }: { row: Row<RelationshipUi> }) => (
                <div className="govuk-checkboxes govuk-checkboxes--small">
                    <CheckBox
                        label=""
                        aria-label={t('table.selectItem', {
                            itemName: t('relationshipList.selectRowCheckboxLabel', {
                                startName: row.original.startName,
                                endName: row.original.endName,
                            }),
                        })}
                        name="checkbox"
                        id={`checkbox_${row.id}`}
                        value="true"
                        onChange={row.getToggleSelectedHandler()}
                        onClick={(event) => event.stopPropagation()}
                        checked={row.getIsSelected()}
                    />
                </div>
            ),
        },
        {
            id: 'summary',
            header: t('relationshipList.summary'),
            accessorFn: (row) => row,
            cell: (row) => (
                <a
                    href="#"
                    onClick={(event) => {
                        event.preventDefault()
                        setRelationData(row.row.original)
                    }}
                >
                    <Trans
                        i18nKey="relationshipList.summaryValue"
                        components={{
                            strong: <strong />,
                        }}
                        values={{
                            startName: row.row.original.startName,
                            endName: row.row.original.endName,
                        }}
                    />
                </a>
            ),
            size: 300,
            enableSorting: false,
        },
        {
            id: 'createdAt',
            header: t('relationshipList.createdAt'),
            accessorFn: (row) => row.metaAttributes?.createdAt,
            meta: {
                getCellContext: (ctx: CellContext<RelationshipUi, unknown>) => t('date', { date: ctx.getValue() as string }),
            },
            cell: (row) => t('date', { date: row.getValue() as string }),
            enableSorting: true,
        },
        {
            id: 'startCiTypeName',
            header: t('relationshipList.startTypeName'),
            accessorFn: (row) => row.startTypeName,
            meta: {
                getCellContext: (ctx: CellContext<RelationshipUi, unknown>) => ctx?.getValue?.(),
            },
            enableSorting: false,
        },
        {
            id: 'startCiName',
            header: t('relationshipList.startName'),
            size: 200,
            accessorFn: (row) => row.startName,
            meta: {
                getCellContext: (ctx: CellContext<RelationshipUi, unknown>) => ctx?.getValue?.(),
            },
            enableSorting: true,
        },
        {
            id: 'startKodMetaIS',
            header: t('relationshipList.startKodMetaIS'),
            accessorFn: (row) => row.startKodMetaIS,
            meta: {
                getCellContext: (ctx: CellContext<RelationshipUi, unknown>) => ctx?.getValue?.(),
            },
            enableSorting: false,
        },
        {
            id: 'endCiTypeName',
            header: t('relationshipList.endTypeName'),
            accessorFn: (row) => row.endTypeName,
            meta: {
                getCellContext: (ctx: CellContext<RelationshipUi, unknown>) => ctx?.getValue?.(),
            },
            enableSorting: false,
        },
        {
            id: 'endCiName',
            header: t('relationshipList.endName'),
            size: 200,
            accessorFn: (row) => row.endName,
            meta: {
                getCellContext: (ctx: CellContext<RelationshipUi, unknown>) => ctx?.getValue?.(),
            },
            enableSorting: true,
        },
        {
            id: 'endKodMetaIS',
            header: t('relationshipList.endKodMetaIS'),
            accessorFn: (row) => row.endKodMetaIS,
            meta: {
                getCellContext: (ctx: CellContext<RelationshipUi, unknown>) => ctx?.getValue?.(),
            },
            enableSorting: false,
        },
    ]

    return (
        <>
            <BreadCrumbs
                withWidthContainer
                links={[
                    { label: t('tasks.home'), href: '/', icon: HomeIcon },
                    { label: getHowToTranslate(HowTo.EGOV_HOWTO, t), href: RouteNames.HOW_TO_EGOV_COMPONENTS },
                    { label: t(`titles.relation.${technicalName}`), href: RouterRoutes.RELATION_LIST },
                ]}
            />
            <MainContentWrapper>
                <TextHeading size="XL">{t(`titles.relation.${technicalName}`)}</TextHeading>
                <ElementToScrollTo trigger={isActionSuccess.value}>
                    <MutationFeedback success={isActionSuccess.value} successMessage={t('exportItemsOrRelations.exportSuccess')} />
                </ElementToScrollTo>
                <Filter<RelationFilterData, ColumnsOutputDefinition>
                    defaultFilterValues={defaultFilterValues}
                    rowSelection={rowSelection}
                    form={({ filter, setValue, isOpen }) => {
                        return (
                            <div>
                                <CiLazySelect
                                    ciType={sourceTechName ?? ''}
                                    label={sourceName ?? ''}
                                    isMulti
                                    setValue={setValue}
                                    name="startCIs"
                                    defaultValue={filter.startCIs ?? []}
                                />
                                <CiLazySelect
                                    ciType={targetTechName ?? ''}
                                    label={targetName ?? ''}
                                    isMulti
                                    setValue={setValue}
                                    name="endCIs"
                                    defaultValue={filter.endCIs ?? []}
                                />
                                <DynamicFilterAttributes
                                    setValue={setValue}
                                    defaults={defaultFilterValues}
                                    filterData={{
                                        attributeFilters: filter.attributeFilters ?? {},
                                        metaAttributeFilters: filter.metaAttributeFilters ?? {},
                                    }}
                                    ciName={technicalName}
                                    entityName={technicalName}
                                    attributes={attributes as ExtendedAttribute[]}
                                    attributeProfiles={attributeProfiles}
                                    constraintsData={constraintsData}
                                    isFocusable={isOpen}
                                />
                            </div>
                        )
                    }}
                />

                <QueryFeedback loading={isLoading} withChildren error={isError}>
                    <ActionsOverTable
                        entityName={technicalName ?? ''}
                        pagination={{
                            dataLength: totalItems,
                            totalPages: totalPages,
                            pageNumber: defFilter.pageNumber ?? BASE_PAGE_NUMBER,
                            pageSize: defFilter.pageSize ?? BASE_PAGE_SIZE,
                        }}
                        handleFilterChange={handleFilterChange}
                        pagingOptions={DEFAULT_PAGESIZE_OPTIONS}
                        hiddenButtons={{ [ActionNames.SELECT_COLUMNS]: true }}
                        exportButton={<RelationsExport filter={exportPayload} path={`/relation/${technicalName}`} selectedUuids={rowSelection.ids} />}
                        importButton={<ImportButton checkRequestId doCleanUp ciType={sourceTechName ?? ''} />}
                    />

                    <Table
                        tableRef={tableRef}
                        data={relations}
                        columns={columns}
                        isLoading={isLoading}
                        sort={defFilter.sort}
                        onSortingChange={(columnSort) => {
                            rowSelection.clearSelectedRows()
                            handleFilterChange({ sort: columnSort })
                        }}
                        getRowId={(originalRow) => `${originalRow.uuid}`}
                        rowSelection={rowSelection.rowSelection}
                        onRowSelectionChange={rowSelection.getOnRowSelectionChange()}
                    />
                    <PaginatorWrapper
                        pageNumber={defFilter.pageNumber || BASE_PAGE_NUMBER}
                        pageSize={defFilter.pageSize || BASE_PAGE_SIZE}
                        dataLength={totalItems || 0}
                        totalPages={totalPages}
                        handlePageChange={(filter) => {
                            handleFilterChange(filter)
                            tableRef.current?.scrollIntoView({ behavior: 'smooth' })
                        }}
                    />
                </QueryFeedback>
            </MainContentWrapper>
            <BaseModal isOpen={relationData !== null} close={() => setRelationData(null)}>
                <div>
                    {relationData && (
                        <>
                            <RelationDetailModalView
                                data={{ relationshipData, relationTypeData, constraintsData, ciSourceData, ciTargetData, ownerData, unitsData }}
                                isLoading={isDetailLoading}
                                isError={isDetailError}
                                refetchRelationship={refetchRelationship}
                            />
                        </>
                    )}
                </div>
                <ModalButtons onClose={() => setRelationData(null)} />
            </BaseModal>
        </>
    )
}
